import * as React from "react";
import { Box, Card, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { LockSimpleIcon, SparkleIcon } from "@phosphor-icons/react";

import type { LeadSearch } from "@/hooks/api/use-lead-finder";

import { tint } from "./tint";

type ListKey =
	| "jobTitles"
	| "excludeJobTitles"
	| "managementLevels"
	| "departments"
	| "industries"
	| "companyKeywords"
	| "countries"
	| "states"
	| "metroAreas"
	| "postalCodes";

/** The search's filters by what they say: who the people are, where, and at what kind of company. */
const GROUPS: { label: string; keys: ListKey[] }[] = [
	{ label: "Who", keys: ["jobTitles", "excludeJobTitles", "managementLevels", "departments"] },
	{ label: "Where", keys: ["countries", "states", "metroAreas", "postalCodes"] },
	{ label: "Company", keys: ["industries", "companyKeywords"] },
];

/** The search is built on these: one of each always stays. */
const KEEP_ONE = new Set<ListKey>(["jobTitles", "countries"]);
/** A city outside the US and Canada can take dozens of postcodes; the rest fold into one chip. */
const POSTCODES_SHOWN = 8;
const ALWAYS = ["Has a phone", "Accuracy 85+", "Do Not Call left out", "Not already a contact"];

/** The finder's choices: tinted, and removable. */
const chosen = (theme: any) => ({
	borderRadius: "8px",
	...tint("primary", true)(theme),
	"& .MuiChip-deleteIcon": { color: "inherit", opacity: 0.6, "&:hover": { color: "inherit", opacity: 1 } },
});

const COMPANY_TYPE: Record<string, string> = {
	private: "Private",
	public: "Public",
	npo: "Non-profit",
	education: "Education",
	government: "Government",
};

/** The company news a search can ask for, as the lead finder names it. */
const EVENT: Record<string, string> = {
	expanding: "Opening or moving sites",
	hiring: "Hiring plans",
	acquisition: "Merger or acquisition",
	launch: "Product launch",
	partnership: "New partnership",
	project: "New project",
	painPoint: "Pain point",
};

/** Revenue in ZoomInfo's millions of dollars, as people say it. */
const dollars = (millions: number) => (millions >= 1000 ? `$${millions / 1000}B` : `$${millions}M`);

function revenue(search: LeadSearch) {
	const { revenueMinMillions: min, revenueMaxMillions: max } = search;
	if (min && max) return `Revenue ${dollars(min)}–${dollars(max)}`;
	if (min) return `Revenue ${dollars(min)}+`;
	if (max) return `Revenue up to ${dollars(max)}`;
	return null;
}

function size(search: LeadSearch) {
	const { employeesMin: min, employeesMax: max } = search;
	if (min && max) return `${min.toLocaleString()}-${max.toLocaleString()} employees`;
	if (min) return `${min.toLocaleString()}+ employees`;
	if (max) return `Up to ${max.toLocaleString()} employees`;
	return null;
}

export interface SearchSummaryProps {
	search: LeadSearch;
	unknown: string[];
	busy: boolean;
	/** Search again as edited; free. */
	onEdit: (search: LeadSearch) => void;
}

export function SearchSummary({ search, unknown, busy, onEdit }: SearchSummaryProps): React.JSX.Element {
	const remove = (key: ListKey, value: string) =>
		onEdit({ ...search, [key]: search[key].filter((item) => item !== value) });
	const companySize = size(search);
	const companyRevenue = revenue(search);
	/** A single-value filter as a removable chip; removing it clears what `clear` names. */
	const signal = (label: string | null, clear: Partial<LeadSearch>) =>
		label && (
			<Chip
				key={label}
				label={label}
				size="small"
				disabled={busy}
				sx={chosen}
				onDelete={() => onEdit({ ...search, ...clear })}
			/>
		);
	const growth = signal(search.headcountGrowthMin ? `Grew ${search.headcountGrowthMin}%+ last year` : null, {
		headcountGrowthMin: null,
	});
	const funded = signal(
		search.fundedWithinMonths ? `Raised funding · last ${search.fundedWithinMonths} months` : null,
		{ fundedWithinMonths: null }
	);
	const events = search.events ?? [];
	const newInRole = signal(
		search.newInRoleWithinDays ? `New in role · last ${search.newInRoleWithinDays} days` : null,
		{ newInRoleWithinDays: null }
	);

	const chips = (key: ListKey) => {
		const values = search[key];
		const shown = key === "postalCodes" ? values.slice(0, POSTCODES_SHOWN) : values;
		const hidden = values.slice(shown.length);
		return (
			<React.Fragment key={key}>
				{shown.map((value) => (
					<Chip
						key={`${key}:${value}`}
						label={key === "excludeJobTitles" ? `Not ${value}` : value}
						size="small"
						disabled={busy}
						sx={chosen}
						onDelete={KEEP_ONE.has(key) && values.length === 1 ? undefined : () => remove(key, value)}
					/>
				))}
				{hidden.length > 0 && (
					<Tooltip title={hidden.join(", ")}>
						<Chip label={`+${hidden.length} postcodes`} size="small" variant="outlined" sx={{ borderRadius: "8px" }} />
					</Tooltip>
				)}
			</React.Fragment>
		);
	};

	const extras: Record<string, React.ReactNode> = {
		Who: newInRole,
		Company: (
			<>
				{growth}
				{funded}
				{signal(companyRevenue, { revenueMinMillions: null, revenueMaxMillions: null })}
				{(search.companyTypes ?? []).map((type) => (
					<Chip
						key={type}
						label={COMPANY_TYPE[type] ?? type}
						size="small"
						disabled={busy}
						sx={chosen}
						onDelete={() =>
							onEdit({ ...search, companyTypes: (search.companyTypes ?? []).filter((item) => item !== type) })
						}
					/>
				))}
				{companySize && (
					<Chip
						label={companySize}
						size="small"
						disabled={busy}
						sx={chosen}
						onDelete={() => onEdit({ ...search, employeesMin: null, employeesMax: null })}
					/>
				)}
				{search.technologies
					.filter(({ product }) => !unknown.includes(product))
					.map((technology) => (
						<Chip
							key={technology.product}
							label={`Uses ${technology.product}`}
							size="small"
							disabled={busy}
							sx={chosen}
							onDelete={() =>
								onEdit({ ...search, technologies: search.technologies.filter((item) => item !== technology) })
							}
						/>
					))}
				{unknown.map((product) => (
					<Tooltip key={product} title="ZoomInfo does not list this technology, so it is not part of the search.">
						<Chip
							label={`${product} (not in ZoomInfo)`}
							size="small"
							variant="outlined"
							color="warning"
							sx={{ borderRadius: "8px" }}
						/>
					</Tooltip>
				))}
			</>
		),
	};

	const row = (label: string, content: React.ReactNode) => (
		<Stack
			key={label}
			direction={{ xs: "column", sm: "row" }}
			spacing={{ xs: 1, sm: 2 }}
			sx={{ alignItems: { sm: "baseline" } }}
		>
			<Typography
				variant="caption"
				sx={{
					width: 84,
					flexShrink: 0,
					fontWeight: 600,
					letterSpacing: "0.06em",
					textTransform: "uppercase",
					color: "text.secondary",
				}}
			>
				{label}
			</Typography>
			<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>{content}</Box>
		</Stack>
	);

	return (
		<Card sx={{ borderRadius: "16px", p: 3 }}>
			<Stack spacing={2}>
				<Stack
					direction="row"
					spacing={2}
					sx={{ alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap" }}
				>
					<Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16 }}>
						Who we will look for
					</Typography>
					<Stack direction="row" spacing={0.75} sx={{ alignItems: "center", color: "text.secondary" }}>
						<SparkleIcon size={12} weight="fill" color="var(--mui-palette-primary-main)" />
						<Typography variant="caption">Chosen by the lead finder · remove any, or ask for a change</Typography>
					</Stack>
				</Stack>
				{GROUPS.map(({ label, keys }) => {
					const filled = keys.filter((key) => search[key].length > 0);
					const extra = extras[label];
					const extraShown =
						label === "Who"
							? Boolean(newInRole)
							: label === "Company" &&
								Boolean(
									companySize ||
										companyRevenue ||
										growth ||
										funded ||
										(search.companyTypes ?? []).length > 0 ||
										search.technologies.length > 0 ||
										unknown.length > 0
								);
					if (filled.length === 0 && !extraShown) return null;
					return row(
						label,
						<>
							{filled.map((key) => chips(key))}
							{extra}
						</>
					);
				})}
				{events.length > 0 &&
					row(
						"News",
						<>
							{events.map((kind) => {
								const rest = events.filter((item) => item !== kind);
								return (
									<Chip
										key={kind}
										label={EVENT[kind] ?? kind}
										size="small"
										disabled={busy}
										sx={chosen}
										onDelete={() =>
											onEdit({ ...search, events: rest, ...(rest.length === 0 && { eventsWithinMonths: null }) })
										}
									/>
								);
							})}
							<Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
								in the last {search.eventsWithinMonths ?? 6} months
							</Typography>
						</>
					)}
				{row(
					"Always",
					<>
						{ALWAYS.map((rule) => (
							<Chip
								key={rule}
								icon={<LockSimpleIcon size={12} />}
								label={rule}
								size="small"
								variant="outlined"
								sx={{ borderRadius: "8px", color: "text.secondary" }}
							/>
						))}
					</>
				)}
				{search.assumptions.length > 0 && (
					<Typography variant="caption" color="text.secondary">
						Assumed: {search.assumptions.join(" · ")}
					</Typography>
				)}
			</Stack>
		</Card>
	);
}
